import React, {
  useMemo,
  useEffect,
  type ComponentType,
  useCallback,
  type ReactNode,
} from 'react';

import { getAdminConstructionApiUrl } from '@adsk/acs-sc-web-platform-api';
import { CONTEXT_NAMES } from '@adsk/alloy-helpers';
import {
  getOrCreateGlobalContext,
  useStoreWithValidation,
} from '@adsk/alloy-react-context';
import { noop } from '@adsk/alloy-react-helpers';

import { useCurrentProjectId } from './CurrentProjectIdContext';
import { useDeletedUsers } from './DeletedUsers';
import { BaseCombinedStore } from './helpers/createDataModificationStore';
import { warnLegacySetEnabled } from './helpers/warnLegacySetEnabled';
import { useUsersStore } from './stores/UsersStore';
import { User } from './types';

export type UsersContextValue = {
  data: User[];
  loading: boolean;
  error?: null | Error;
  retry(): void;
  addUsers(users: User[]): void;
  updateUser(user: User): void;
  removeUserById(id: User['id']): void;
  removeUserByAutodeskId(autodeskId: User['autodeskId']): void;
  setEnabled?: (enabled: boolean) => void;
};

type FindUserByOptions = {
  includeDeleted?: boolean;
};

const useUsersStoreWithValidation = () =>
  useStoreWithValidation<BaseCombinedStore<User>>(CONTEXT_NAMES.UsersContext, {
    hook: 'useUsers',
    provider: 'UsersProvider',
  });

/**
 * Context for users list.
 */
const UsersContext = getOrCreateGlobalContext<UsersContextValue>(
  CONTEXT_NAMES.UsersContext,
);

const useUsersApiUrl = () => {
  const PAGE_LIMIT = 200;

  const projectId = useCurrentProjectId();
  const adminApiUrl = getAdminConstructionApiUrl();
  return projectId
    ? `${adminApiUrl}/v1/projects/${projectId}/users?limit=${PAGE_LIMIT}`
    : null;
};

const useRemoveUserByAutodeskId = (
  entities: User[],
  removeEntityById: (id: User['id']) => void,
) =>
  useCallback(
    (autodeskId: string) => {
      const userToRemove = entities.find(
        (user) => user.autodeskId === autodeskId,
      );
      if (userToRemove) {
        removeEntityById(userToRemove.id);
      }
    },
    [entities, removeEntityById],
  );

/** Returns the list of users for the current project, along with loading/error state and store mutation helpers. Must be used inside `UsersProvider`. */
export const useUsers = () => {
  const url = useUsersApiUrl();
  const {
    entities,
    error,
    loading,
    fetchPage,
    addEntities,
    updateEntity,
    removeEntityById,
    retry,
  } = useUsersStoreWithValidation();

  const removeUserByAutodeskId = useRemoveUserByAutodeskId(
    entities,
    removeEntityById,
  );

  useEffect(() => {
    fetchPage(url);
  }, [url, fetchPage]);

  return {
    data: entities,
    loading,
    error,
    retry,
    addUsers: addEntities,
    updateUser: updateEntity,
    removeUserById: removeEntityById,
    removeUserByAutodeskId,
    setEnabled: noop,
  };
};

const useFindUserBy = (
  matcher: (user: User) => boolean,
  options: FindUserByOptions = {},
) => {
  const { includeDeleted } = options;
  const {
    data: users,
    loading: areUsersLoading,
    error: usersError,
  } = useUsers();

  const {
    data: deletedUsers,
    loading: areDeletedUsersLoading,
    error: deletedUsersError,
  } = useDeletedUsers({ disable: !includeDeleted });

  const activeUser = useMemo(() => users.find(matcher), [users, matcher]);
  const shouldSearchDeletedUser = !activeUser && includeDeleted;
  const deletedUser = useMemo(
    () => (shouldSearchDeletedUser ? deletedUsers.find(matcher) : undefined),
    [shouldSearchDeletedUser, deletedUsers, matcher],
  );
  const user = activeUser || deletedUser;
  return {
    user,
    loading:
      !user && (areUsersLoading || (includeDeleted && areDeletedUsersLoading)),
    error: !user && (usersError || (includeDeleted && deletedUsersError)),
  };
};

/** Returns a single project user matched by their internal user ID. */
export const useUserById = (id: string, options: FindUserByOptions = {}) => {
  const matcher = useCallback((user: User) => user.id === id, [id]);
  return useFindUserBy(matcher, options);
};

/**
 * Finds a user by their Autodesk ID. Optionally searches deleted users as well.
 */
export const useUserByAutodeskId = (
  autodeskId: string,
  options: FindUserByOptions = {},
) => {
  const matcher = useCallback(
    (user: User) => user.autodeskId === autodeskId,
    [autodeskId],
  );
  return useFindUserBy(matcher, options);
};

const useUsersProvider = () => {
  const url = useUsersApiUrl();
  const {
    entities,
    error,
    loading,
    addEntities,
    updateEntity,
    removeEntityById,
    retry,
    fetchPage,
  } = useUsersStore();

  const removeUserByAutodeskId = useRemoveUserByAutodeskId(
    entities,
    removeEntityById,
  );

  // Trigger fetch on setEnabled for legacy provider backwards compatibility
  const legacySetEnabled = useCallback(() => {
    warnLegacySetEnabled(CONTEXT_NAMES.UsersContext);
    fetchPage(url);
  }, [url, fetchPage]);

  return useMemo(
    () => ({
      data: entities,
      loading,
      error,
      retry,
      addUsers: addEntities,
      updateUser: updateEntity,
      removeUserById: removeEntityById,
      removeUserByAutodeskId,
      setEnabled: legacySetEnabled,
    }),
    [
      entities,
      loading,
      error,
      retry,
      addEntities,
      updateEntity,
      removeEntityById,
      removeUserByAutodeskId,
      legacySetEnabled,
    ],
  );
};

type UsersProviderProps = {
  children?: ReactNode;
};

export const UsersProvider: ComponentType<UsersProviderProps> = ({
  children,
}) => {
  const value = useUsersProvider();

  return (
    <UsersContext.Provider value={value}>{children}</UsersContext.Provider>
  );
};

UsersProvider.displayName = 'UsersProvider';
